import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
  X,
  User as UserIcon,
  Mail,
  Phone,
  Lock,
  ArrowLeft,
  ChevronDown,
  Trash2,
} from "lucide-react";
import {
  CreateUserRequest,
  UpdateUserRequest,
  User,
} from "../../api/services/user/type";
import { useRoles } from "../../hooks/useRoles";
import CustomInput from "../ui/CustomInput";
import PrimaryButton from "../ui/PrimaryButton";
import { ConfirmDialog } from "../ui/ConfirmDialog";

interface UserFormProps {
  user?: User;
  onSubmit: (data: CreateUserRequest | UpdateUserRequest) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

interface FormData {
  userId: string;
  name: string;
  email: string;
  phone: string;
  roleIds: string[];
  username: string;
  password: string;
  confirmPassword: string;
}

export const UserForm: React.FC<UserFormProps> = ({
  user,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const isEditMode = !!user;

  const { data: rolesData, isLoading: rolesLoading } = useRoles({
    limit: 50,
  });

  const roles = rolesData?.roles || [];

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    setError,
    clearErrors,
    trigger,
  } = useForm<FormData>({
    defaultValues: {
      userId: user?.userId || "",
      name: user?.name || "",
      email: user?.email || "",
      phone: user?.phone || "",
      roleIds: user?.assignedRoles?.map((role) => role.id) || [],
      username: user?.username || "",
      password: "",
      confirmPassword: "",
    },
  });

  const watchedValues = watch();

  const validateStep1 = async () => {
    const fieldsToValidate: (keyof FormData)[] = [
      "userId",
      "name",
      "email",
      "phone",
    ];
    const result = await trigger(fieldsToValidate);

    return result;
  };

  const validateStep2 = async () => {
    const fieldsToValidate: (keyof FormData)[] = [
      "username",
      "password",
      "confirmPassword",
    ];
    const result = await trigger(fieldsToValidate);

    if (watchedValues.roleIds?.length === 0) {
      setError("roleIds", { message: "Please select at least one role" });
      return false;
    } else {
      clearErrors("roleIds");
    }

    return result;
  };

  const handleNext = async () => {
    const isValid = await validateStep1();
    if (isValid) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handleCancelClick = () => {
    setShowCancelDialog(true);
  };

  const handleConfirmCancel = () => {
    setShowCancelDialog(false);
    onCancel();
  };

  const handleCancelDialog = () => {
    setShowCancelDialog(false);
  };

  const onFormSubmit = async (data: FormData) => {
    if (currentStep === 1) {
      handleNext();
      return;
    }

    const isValid = await validateStep2();
    if (isValid) {
      if (isEditMode) {
        const updateData: UpdateUserRequest = {
          name: data.name.trim(),
          email: data.email.trim(),
          phone: data.phone.trim() || undefined,
          username: data.username.trim(),
          roleIds: data.roleIds,
        };

        if (data.password.trim()) {
          updateData.password = data.password;
        }

        onSubmit(updateData);
      } else {
        const userData: CreateUserRequest = {
          userId: data.userId.trim(),
          name: data.name.trim(),
          email: data.email.trim(),
          phone: data.phone.trim() || undefined,
          username: data.username.trim(),
          password: data.password,
          roleIds: data.roleIds,
        };
        onSubmit(userData);
      }
    }
  };

  const handleRoleToggle = (roleId: string) => {
    const currentRoles = watchedValues.roleIds || [];
    let newRoles: string[];

    if (currentRoles.includes(roleId)) {
      newRoles = currentRoles.filter((id) => id !== roleId);
    } else {
      newRoles = [...currentRoles, roleId];
    }

    setValue("roleIds", newRoles);

    if (newRoles.length > 0) {
      clearErrors("roleIds");
    }
  };

  const getSelectedRolesText = () => {
    const selectedRoles = watchedValues.roleIds || [];
    if (selectedRoles.length === 0) return "Select roles";
    if (selectedRoles.length === 1) {
      const role = roles.find((r) => r.id === selectedRoles[0]);
      return role?.name || "Unknown role";
    }
    return `${selectedRoles.length} roles selected`;
  };

  const getRoleBadgeColor = (roleName: string) => {
    switch (roleName) {
      case "SUPERADMIN":
        return "bg-red-600";
      case "SUPERVISOR":
        return "bg-blue-600";
      case "DEALER":
        return "bg-green-600";
      case "CASHIER":
        return "bg-yellow-600";
      case "WATCHER":
        return "bg-purple-600";
      default:
        return "bg-gray-600";
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto border border-slate-700">
        {/* Header */}
        <div className="sticky top-0 bg-slate-800 p-6 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">
              {isEditMode ? "Edit User" : "Add New User"}
            </h2>
            <p className="text-slate-400 text-sm mt-1">
              Step {currentStep} of 2:{" "}
              {currentStep === 1 ? "Basic Information" : "Role & Account Setup"}
            </p>
          </div>
          <button
            onClick={handleCancelClick}
            className="text-slate-400 hover:text-white transition-colors"
            disabled={isLoading}
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-6">
          {currentStep === 1 && (
            <div className="space-y-4">
              {/* Full Name */}
              <CustomInput
                label="Full Name"
                type="text"
                placeholder="Enter user's full name"
                register={register}
                name="name"
                validation={{
                  required: "Full name is required",
                  validate: (v: string | string[]) => {
                    if (Array.isArray(v)) return true;
                    const trimmed = v.trim();
                    if (trimmed.length === 0) return "Full name is required";
                    if (!/^[a-zA-Z\s]+$/.test(trimmed)) {
                      return "Name can only contain letters and spaces";
                    }

                    if (trimmed.length < 2) {
                      return "Name must be at least 2 characters";
                    }

                    if (/\s{2,}/.test(trimmed)) {
                      return "Name cannot contain multiple consecutive spaces";
                    }

                    if (trimmed !== v.trim()) {
                      return "Name cannot start or end with spaces";
                    }

                    return true;
                  },
                }}
                error={errors.name}
                disabled={isLoading}
                icon={<UserIcon className="w-5 h-5 text-slate-400" />}
              />
              {/* Email */}
              <CustomInput
                label="Email Address"
                type="text"
                placeholder="Enter email address"
                register={register}
                name="email"
                validation={{
                  required: "Email address is required",
                  validate: (v: string | string[]) => {
                    if (Array.isArray(v)) return true;
                    const trimmed = v.trim();
                    if (trimmed.length === 0)
                      return "Email address is required";

                    const emailRegex =
                      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                    if (!emailRegex.test(trimmed)) {
                      return "Please enter a valid email address";
                    }

                    const parts = trimmed.split("@");
                    if (parts.length !== 2) {
                      return "Please enter a valid email address";
                    }

                    if (trimmed.includes("..")) {
                      return "Email cannot contain consecutive dots";
                    }

                    return true;
                  },
                }}
                error={errors.email}
                disabled={isLoading}
                icon={<Mail className="w-5 h-5 text-slate-400" />}
              />
              {/* Phone */}
              <CustomInput
                label="Phone Number (Optional)"
                type="tel"
                placeholder="Enter phone number"
                register={register}
                name="phone"
                validation={{
                  validate: (v: string | string[]) => {
                    if (Array.isArray(v)) return true;

                    if (!v || v.trim() === "") return true;
                    const trimmed = v.trim();
                    if (!/^\d+$/.test(trimmed)) {
                      return "Phone must contain numbers only";
                    }
                    if (trimmed.length < 7) {
                      return "Phone number must be at least 7 digits";
                    }
                  },
                }}
                error={errors.phone}
                disabled={isLoading}
                icon={<Phone className="w-5 h-5 text-slate-400" />}
              />
            </div>
          )}
          {currentStep === 2 && (
            <div className="space-y-4">
              {/* Username */}
              <CustomInput
                label="Username"
                type="text"
                placeholder="Enter unique username"
                register={register}
                name="username"
                validation={{
                  required: "Username is required",
                  validate: (v: string | string[]) => {
                    if (Array.isArray(v)) return true;

                    const trimmed = v.trim();
                    if (trimmed.length === 0) return "Username is required";
                    if (trimmed.length < 3) {
                      return "Username must be at least 3 characters";
                    }
                    if (!/^[a-zA-Z0-9_]+$/.test(trimmed)) {
                      return "Username can only contain letters, numbers, and underscores";
                    }
                  },
                }}
                error={errors.username}
                disabled={isLoading}
                icon={<UserIcon className="w-5 h-5 text-slate-400" />}
              />

              {/* Role Selection Dropdown */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Role Assignment
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                    disabled={isLoading || rolesLoading}
                    className={`w-full px-4 py-3 bg-slate-700 border rounded-lg text-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between ${
                      errors.roleIds ? "border-red-500" : "border-slate-600"
                    }`}
                  >
                    <span
                      className={
                        (watchedValues.roleIds?.length || 0) === 0
                          ? "text-slate-400"
                          : "text-white"
                      }
                    >
                      {rolesLoading
                        ? "Loading roles..."
                        : getSelectedRolesText()}
                    </span>
                    <ChevronDown
                      size={20}
                      className={`text-slate-400 transition-transform ${
                        isRoleDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {isRoleDropdownOpen && !rolesLoading && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-slate-700 border border-slate-600 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                      {roles.map((role) => (
                        <label
                          key={role.id}
                          className="flex items-center space-x-3 p-3 hover:bg-slate-600 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={(watchedValues.roleIds || []).includes(
                              role.id,
                            )}
                            onChange={() => handleRoleToggle(role.id)}
                            className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
                          />
                          <div className="flex items-center space-x-2 flex-1">
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white ${getRoleBadgeColor(role.name)}`}
                            >
                              {role.name}
                            </span>
                            <div className="flex-1">
                              <div className="text-slate-400 text-xs">
                                {role.description}
                              </div>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {errors.roleIds && (
                  <p className="text-red-400 text-sm mt-1">
                    {errors.roleIds.message}
                  </p>
                )}
              </div>

              {/* Selected Roles Display */}
              {(watchedValues.roleIds?.length || 0) > 0 && (
                <div className="bg-slate-700 rounded-lg p-3 border border-slate-600">
                  <h4 className="text-sm font-medium text-slate-300 mb-2">
                    Selected Roles ({watchedValues.roleIds?.length || 0})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {(watchedValues.roleIds || []).map((roleId) => {
                      const role = roles.find((r) => r.id === roleId);
                      return role ? (
                        <span
                          key={roleId}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${getRoleBadgeColor(role.name)}`}
                        >
                          {role.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {/* Password */}
              <CustomInput
                label={
                  isEditMode
                    ? "New Password (Leave blank to keep current)"
                    : "Password"
                }
                type="password"
                placeholder={
                  isEditMode ? "Enter new password" : "Enter secure password"
                }
                register={register}
                name="password"
                validation={{
                  required: isEditMode ? false : "Password is required",
                  validate: (v: string | string[]) => {
                    if (Array.isArray(v)) return true;

                    if (isEditMode && (!v || v.trim() === "")) return true;

                    if (v.length < 6) {
                      return "Password must be at least 6 characters";
                    }
                    if (!/(?=.*[a-z])(?=.*\d)/.test(v)) {
                      return "Password must contain at least one lowercase letter and one number";
                    }
                  },
                }}
                error={errors.password}
                disabled={isLoading}
                icon={<Lock className="w-5 h-5 text-slate-400" />}
              />

              <CustomInput
                label="Confirm Password"
                type="password"
                placeholder="Confirm your password"
                register={register}
                name="confirmPassword"
                validation={{
                  required: isEditMode ? false : "Please confirm your password",
                  validate: (v: string | string[]) => {
                    if (Array.isArray(v)) return true;

                    const password = watchedValues.password;

                    if (isEditMode && (!password || password.trim() === "")) {
                      return true;
                    }

                    if (v !== password) {
                      return "Passwords do not match";
                    }
                  },
                }}
                error={errors.confirmPassword}
                disabled={isLoading}
                icon={<Lock className="w-5 h-5 text-slate-400" />}
              />
            </div>
          )}
          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            {currentStep === 2 && (
              <button
                type="button"
                onClick={handleBack}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700 hover:border-slate-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            )}

            <PrimaryButton
              type="submit"
              isLoading={isLoading}
              loadingText={currentStep === 1 ? "Next..." : "Creating..."}
              className="min-w-[120px]"
            >
              {currentStep === 1 ? (
                <>Next</>
              ) : isEditMode ? (
                "Update User"
              ) : (
                "Create User"
              )}
            </PrimaryButton>
          </div>
        </form>
      </div>

      {/* Dropdown backdrop */}
      {isRoleDropdownOpen && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setIsRoleDropdownOpen(false)}
        />
      )}

      <ConfirmDialog
        open={showCancelDialog}
        title="Confirm Cancellation"
        description="Are you sure you want to cancel creating this user? All entered information will be lost."
        icon={<Trash2 className="h-8 w-8 text-white" />}
        iconBgColor="bg-red-600"
        confirmColor="bg-red-600 hover:bg-red-700"
        confirmText="Yes, Cancel"
        cancelText="Continue Editing"
        onCancel={handleCancelDialog}
        onConfirm={handleConfirmCancel}
      />
    </div>
  );
};
